import { Component } from '@theme/component';
import { fetchConfig, preloadImage } from '@theme/utilities';
import { ThemeEvents, CartAddEvent, CartErrorEvent, CartUpdateEvent, VariantUpdateEvent } from '@theme/events';
import { cartPerformance } from '@theme/performance';
import { morph } from '@theme/morph';

export const ADD_TO_CART_TEXT_ANIMATION_DURATION = 2000;

// Error message display duration - gives users time to read the message
const ERROR_MESSAGE_DISPLAY_DURATION = 10000;

// Button re-enable delay after error - prevents rapid repeat attempts
const ERROR_BUTTON_REENABLE_DELAY = 1000;

// Success message display duration for screen readers
const SUCCESS_MESSAGE_DISPLAY_DURATION = 5000;

/**
 * A custom element that manages an add to cart button.
 *
 * @typedef {object} AddToCartRefs
 * @property {HTMLButtonElement} addToCartButton - The add to cart button.
 * @extends Component<AddToCartRefs>
 */
export class AddToCartComponent extends Component {
  requiredRefs = ['addToCartButton'];

  /** @type {number | undefined} */
  #animationTimeout;

  /** @type {number | undefined} */
  #cleanupTimeout;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('pointerenter', this.#preloadImage);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    if (this.#animationTimeout) clearTimeout(this.#animationTimeout);
    if (this.#cleanupTimeout) clearTimeout(this.#cleanupTimeout);
    this.removeEventListener('pointerenter', this.#preloadImage);
  }

  /**
   * Disables the add to cart button.
   */
  disable() {
    this.refs.addToCartButton.disabled = true;
  }

  /**
   * Enables the add to cart button.
   */
  enable() {
    this.refs.addToCartButton.disabled = false;
  }

  /**
   * Handles the click event for the add to cart button.
   * @param {MouseEvent & {target: HTMLElement}} event - The click event.
   */
  handleClick(event) {
    const form = this.closest('form');
    if (!form?.checkValidity()) return;

    const quantitySelector = /** @type {any} */ (
      form.querySelector('quantity-selector-component')
    );

    if (quantitySelector?.canAddToCart) {
      const validation = quantitySelector.canAddToCart();

      if (!validation.canAdd) {
        return;
      }
    }

    this.animateAddToCart();
  }

  #preloadImage = () => {
    const image = this.dataset.productVariantMedia;

    if (!image) return;

    preloadImage(image);
  };

  /**
   * Animates the add to cart button.
   */
  animateAddToCart() {
    const { addToCartButton } = this.refs;

    if (this.#animationTimeout) {
      clearTimeout(this.#animationTimeout);
    }

    if (this.#cleanupTimeout) {
      clearTimeout(this.#cleanupTimeout);
    }

    if (!addToCartButton.classList.contains('atc-added')) {
      addToCartButton.classList.add('atc-added');
    }

    this.#animationTimeout = setTimeout(() => {
      this.#cleanupTimeout = setTimeout(() => {
        this.refs.addToCartButton.classList.remove('atc-added');
      }, 10);
    }, ADD_TO_CART_TEXT_ANIMATION_DURATION);
  }
}

if (!customElements.get('add-to-cart-component')) {
  customElements.define(
    'add-to-cart-component',
    AddToCartComponent
  );
}

/**
 * A custom element that manages a product form.
 *
 * @typedef {object} ProductFormRefs
 * @property {HTMLInputElement} variantId - The form input for submitting the variant ID.
 * @property {AddToCartComponent | undefined} addToCartButtonContainer - The add to cart button container element.
 * @property {HTMLElement | undefined} addToCartTextError - The add to cart text error.
 * @property {HTMLElement | undefined} acceleratedCheckoutButtonContainer - The accelerated checkout button container element.
 * @property {HTMLElement} liveRegion - The live region.
 * @property {HTMLElement | undefined} quantityLabelCartCount - The quantity label cart count element.
 * @property {HTMLElement | undefined} quantityRules - The quantity rules element.
 * @property {HTMLElement | undefined} productFormButtons - The product form buttons container.
 *
 * @extends Component<ProductFormRefs>
 */
class ProductFormComponent extends Component {
  requiredRefs = ['variantId', 'liveRegion'];

  #abortController = new AbortController();

  /**
   * Hard submission lock.
   * Prevents multiple /cart/add requests from the same form.
   */
  #isSubmitting = false;

  /** @type {number | undefined} */
  #timeout;

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#abortController;

    const target = this.closest(
      '.shopify-section, dialog, product-card'
    );

    target?.addEventListener(
      ThemeEvents.variantUpdate,
      this.#onVariantUpdate,
      { signal }
    );

    target?.addEventListener(
      ThemeEvents.variantSelected,
      this.#onVariantSelected,
      { signal }
    );

    // Keep quantity state synchronized when cart changes elsewhere.
    document.addEventListener(
      ThemeEvents.cartUpdate,
      this.#onCartUpdate,
      { signal }
    );
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
  }

  /**
   * Fetches cart and updates quantity selector for current variant.
   *
   * This does NOT add anything to the cart.
   * It only reads /cart.js.
   *
   * @returns {Promise<number>}
   */
  async #fetchAndUpdateCartQuantity() {
    const variantIdInput =
      /** @type {HTMLInputElement | null} */ (
        this.querySelector('input[name="id"]')
      );

    if (!variantIdInput?.value) {
      return 0;
    }

    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();

      const cartItem = cart.items.find(
        /** @param {any} item */
        (item) =>
          item.variant_id.toString() ===
          variantIdInput.value.toString()
      );

      const cartQty = cartItem
        ? cartItem.quantity
        : 0;

      const quantitySelector =
        /** @type {any} */ (
          this.querySelector(
            'quantity-selector-component'
          )
        );

      if (quantitySelector?.setCartQuantity) {
        quantitySelector.setCartQuantity(
          cartQty
        );
      }

      this.#updateQuantityLabel(cartQty);

      return cartQty;
    } catch (error) {
      console.error(
        'Failed to fetch cart quantity:',
        error
      );

      return 0;
    }
  }

  /**
   * Sync quantity when cart was updated somewhere else.
   *
   * Ignore our own successful product-form event so we
   * don't respond to the event we just dispatched.
   *
   * @param {CartUpdateEvent|CartAddEvent} event
   */
  #onCartUpdate = async (event) => {
    if (
      event.detail?.sourceId === this.id ||
      event.detail?.data?.source ===
        'product-form-component'
    ) {
      return;
    }

    await this.#fetchAndUpdateCartQuantity();
  };

  /**
   * Handles product form submission.
   *
   * @param {Event} event
   */
  handleSubmit(event) {
    const {
      addToCartTextError,
      addToCartButtonContainer,
    } = this.refs;

    // Never allow the browser to perform a normal form navigation.
    event.preventDefault();

    /**
     * HARD LOCK
     *
     * Custom ATC / Buy Now may call requestSubmit(),
     * but while one request is already running,
     * another submission is ignored.
     */
    if (this.#isSubmitting) {
      return;
    }

    this.#isSubmitting = true;

    if (this.#timeout) {
      clearTimeout(this.#timeout);
    }

    /**
     * If Shopify's native ATC is disabled,
     * stop here and release the lock.
     */
    if (
      addToCartButtonContainer
        ?.refs
        .addToCartButton
        ?.disabled
    ) {
      this.#isSubmitting = false;
      return;
    }

    const form = this.querySelector('form');

    if (!form) {
      this.#isSubmitting = false;
      throw new Error(
        'Product form element missing'
      );
    }

    /**
     * Validate quantity rules before sending anything.
     */
    const quantitySelector =
      /** @type {any} */ (
        this.querySelector(
          'quantity-selector-component'
        )
      );

    if (quantitySelector?.canAddToCart) {
      const validation =
        quantitySelector.canAddToCart();

      if (!validation.canAdd) {
        addToCartButtonContainer?.disable();

        const errorTemplate =
          this.dataset.quantityErrorMax || '';

        const errorMessage =
          errorTemplate.replace(
            '{{ maximum }}',
            validation.maxQuantity.toString()
          );

        if (addToCartTextError) {
          addToCartTextError.classList.remove(
            'hidden'
          );

          const textNode =
            addToCartTextError.childNodes[2];

          if (textNode) {
            textNode.textContent =
              errorMessage;
          } else {
            const newTextNode =
              document.createTextNode(
                errorMessage
              );

            addToCartTextError.appendChild(
              newTextNode
            );
          }

          this.#setLiveRegionText(
            errorMessage
          );

          if (this.#timeout) {
            clearTimeout(this.#timeout);
          }

          this.#timeout = setTimeout(() => {
            if (!addToCartTextError) {
              return;
            }

            addToCartTextError.classList.add(
              'hidden'
            );

            this.#clearLiveRegionText();
          }, ERROR_MESSAGE_DISPLAY_DURATION);
        }

        setTimeout(() => {
          addToCartButtonContainer?.enable();
        }, ERROR_BUTTON_REENABLE_DELAY);

        this.#isSubmitting = false;

        return;
      }
    }

    /**
     * Build ONE Shopify cart/add request.
     */
    const formData = new FormData(form);

    /**
     * Collect all Shopify cart section IDs first.
     *
     * IMPORTANT:
     * We append "sections" exactly once.
     */
    const cartItemsComponents =
      document.querySelectorAll(
        'cart-items-component'
      );

    const cartItemComponentsSectionIds = [];

    cartItemsComponents.forEach((item) => {
      if (
        item instanceof HTMLElement &&
        item.dataset.sectionId
      ) {
        cartItemComponentsSectionIds.push(
          item.dataset.sectionId
        );
      }
    });

    if (
      cartItemComponentsSectionIds.length > 0
    ) {
      formData.append(
        'sections',
        cartItemComponentsSectionIds.join(',')
      );
    }

    const fetchCfg = fetchConfig(
      'javascript',
      {
        body: formData,
      }
    );

    /**
     * ONE /cart/add request.
     */
    fetch(Theme.routes.cart_add_url, {
      ...fetchCfg,

      headers: {
        ...fetchCfg.headers,
        Accept: 'text/html',
      },
    })
      .then((response) =>
        response.json()
      )

      .then((response) => {
        /**
         * Shopify returned an error.
         */
        if (response.status) {
          this.dispatchEvent(
            new CartErrorEvent(
              form.getAttribute('id') || '',
              response.message,
              response.description,
              response.errors
            )
          );

          if (addToCartTextError) {
            addToCartTextError.classList.remove(
              'hidden'
            );

            const textNode =
              addToCartTextError.childNodes[2];

            if (textNode) {
              textNode.textContent =
                response.message;
            } else {
              const newTextNode =
                document.createTextNode(
                  response.message
                );

              addToCartTextError.appendChild(
                newTextNode
              );
            }

            this.#setLiveRegionText(
              response.message
            );

            this.#timeout = setTimeout(() => {
              if (!addToCartTextError) {
                return;
              }

              addToCartTextError.classList.add(
                'hidden'
              );

              this.#clearLiveRegionText();
            }, ERROR_MESSAGE_DISPLAY_DURATION);
          }

          /**
           * Shopify can still add the maximum allowed
           * quantity in certain quantity-limit errors.
           *
           * Preserve Horizon's original cart update
           * behavior for that scenario.
           */
          this.dispatchEvent(
            new CartAddEvent(
              {},
              this.id,
              {
                didError: true,

                source:
                  'product-form-component',

                itemCount:
                  Number(
                    formData.get(
                      'quantity'
                    )
                  ) ||
                  Number(
                    this.dataset
                      .quantityDefault
                  ),

                productId:
                  this.dataset.productId,
              }
            )
          );

          return;
        }

        /**
         * SUCCESS
         */
        const id =
          formData.get('id');

        if (addToCartTextError) {
          addToCartTextError.classList.add(
            'hidden'
          );

          addToCartTextError.removeAttribute(
            'aria-live'
          );
        }

        if (!id) {
          throw new Error(
            'Form ID is required'
          );
        }

        /**
         * Screen reader success announcement.
         */
        if (
          this.refs
            .addToCartButtonContainer
            ?.refs
            .addToCartButton
        ) {
          const addToCartButton =
            this.refs
              .addToCartButtonContainer
              .refs
              .addToCartButton;

          const addedTextElement =
            addToCartButton.querySelector(
              '.add-to-cart-text--added'
            );

          const addedText =
            addedTextElement
              ?.textContent
              ?.trim() ||
            Theme.translations.added;

          this.#setLiveRegionText(
            addedText
          );

          setTimeout(() => {
            this.#clearLiveRegionText();
          }, SUCCESS_MESSAGE_DISPLAY_DURATION);
        }

        /**
         * Read the latest quantity for this variant.
         *
         * This is GET /cart.js only.
         * It does NOT add items.
         */
        this.#fetchAndUpdateCartQuantity();

        /**
         * ONE SUCCESS CART EVENT.
         *
         * Keep resource as {}.
         *
         * Do NOT change this to `response`.
         * The /cart/add response is not the complete
         * cart object expected by every listener.
         *
         * GoKwik can react to this cart:update event
         * and open its drawer.
         */
        this.dispatchEvent(
          new CartAddEvent(
            {},
            id.toString(),
            {
              source:
                'product-form-component',

              itemCount:
                Number(
                  formData.get('quantity')
                ) ||
                Number(
                  this.dataset
                    .quantityDefault
                ),

              productId:
                this.dataset.productId,

              sections:
                response.sections,
            }
          )
        );
      })

      .catch((error) => {
        console.error(
          'Product form add-to-cart error:',
          error
        );
      })

      .finally(() => {
        /**
         * Always release the lock.
         */
        this.#isSubmitting = false;

        cartPerformance.measureFromEvent(
          'add:user-action',
          event
        );
      });
  }

  /**
   * Updates quantity label with current cart quantity.
   *
   * @param {number} cartQty
   */
  #updateQuantityLabel(cartQty) {
    const quantityLabel =
      this.refs.quantityLabelCartCount;

    if (quantityLabel) {
      const inCartText =
        quantityLabel.textContent?.match(
          /\((\d+)\s+(.+)\)/
        );

      if (
        inCartText &&
        inCartText[2]
      ) {
        quantityLabel.textContent =
          `(${cartQty} ${inCartText[2]})`;
      }

      quantityLabel.classList.toggle(
        'hidden',
        cartQty === 0
      );
    }
  }

  /**
   * @param {*} text
   */
  #setLiveRegionText(text) {
    const liveRegion =
      this.refs.liveRegion;

    liveRegion.textContent = text;
  }

  #clearLiveRegionText() {
    const liveRegion =
      this.refs.liveRegion;

    liveRegion.textContent = '';
  }

  /**
   * Morphs or removes/adds an element based on current
   * and new element states.
   *
   * @param {Element | null | undefined} currentElement
   * @param {Element | null | undefined} newElement
   * @param {Element | null} [insertReferenceElement]
   */
  #morphOrUpdateElement(
    currentElement,
    newElement,
    insertReferenceElement = null
  ) {
    if (
      currentElement &&
      newElement
    ) {
      morph(
        currentElement,
        newElement
      );
    } else if (
      currentElement &&
      !newElement
    ) {
      currentElement.remove();
    } else if (
      !currentElement &&
      newElement &&
      insertReferenceElement
    ) {
      insertReferenceElement
        .insertAdjacentElement(
          'beforebegin',
          /** @type {Element} */ (
            newElement.cloneNode(true)
          )
        );
    }
  }

  /**
   * @param {VariantUpdateEvent} event
   */
  #onVariantUpdate = async (event) => {
    if (
      event.detail.data.newProduct
    ) {
      this.dataset.productId =
        event.detail.data.newProduct.id;
    } else if (
      event.detail.data.productId !==
      this.dataset.productId
    ) {
      return;
    }

    const {
      variantId,
      addToCartButtonContainer,
    } = this.refs;

    const currentAddToCartButton =
      addToCartButtonContainer
        ?.refs
        .addToCartButton;

    const newAddToCartButton =
      event.detail.data.html.querySelector(
        '[ref="addToCartButton"]'
      );

    /**
     * Update variant ID.
     */
    variantId.value =
      event.detail.resource?.id ?? '';

    if (
      !currentAddToCartButton &&
      !this.refs
        .acceleratedCheckoutButtonContainer
    ) {
      return;
    }

    /**
     * Update native ATC state.
     */
    if (currentAddToCartButton) {
      if (
        event.detail.resource == null ||
        event.detail.resource.available ==
          false
      ) {
        addToCartButtonContainer.disable();
      } else {
        addToCartButtonContainer.enable();
      }

      if (newAddToCartButton) {
        morph(
          currentAddToCartButton,
          newAddToCartButton
        );
      }
    }

    /**
     * Update accelerated checkout state.
     */
    if (
      this.refs
        .acceleratedCheckoutButtonContainer
    ) {
      if (
        event.detail.resource == null ||
        event.detail.resource.available ==
          false
      ) {
        this.refs
          .acceleratedCheckoutButtonContainer
          ?.setAttribute(
            'hidden',
            'true'
          );
      } else {
        this.refs
          .acceleratedCheckoutButtonContainer
          ?.removeAttribute(
            'hidden'
          );
      }
    }

    /**
     * Update product variant media reference.
     */
    if (event.detail.resource) {
      const productVariantMedia =
        event.detail.resource
          .featured_media
          ?.preview_image
          ?.src;

      if (productVariantMedia) {
        addToCartButtonContainer
          ?.setAttribute(
            'data-product-variant-media',
            productVariantMedia +
              '&width=100'
          );
      }
    }

    /**
     * Update quantity constraints.
     */
    const quantitySelector =
      /** @type {any} */ (
        this.querySelector(
          'quantity-selector-component'
        )
      );

    const newQuantityInput =
      /** @type {HTMLInputElement | null} */ (
        event.detail.data.html.querySelector(
          'quantity-selector-component input[ref="quantityInput"]'
        )
      );

    if (
      quantitySelector
        ?.updateConstraints &&
      newQuantityInput
    ) {
      quantitySelector.updateConstraints(
        newQuantityInput.min,
        newQuantityInput.max || null,
        newQuantityInput.step
      );
    }

    /**
     * Check whether quantity rules appeared/disappeared.
     */
    const quantityRules =
      this.refs.quantityRules;

    const newQuantityRules =
      event.detail.data.html.querySelector(
        '.quantity-rules'
      );

    const isQuantityRulesChanging =
      !!quantityRules !==
      !!newQuantityRules;

    if (
      isQuantityRulesChanging &&
      quantitySelector
    ) {
      const currentQuantityValue =
        quantitySelector.getValue?.();

      const currentProductFormButtons =
        this.refs.productFormButtons;

      const newProductFormButtons =
        event.detail.data.html.querySelector(
          '.product-form-buttons'
        );

      if (
        currentProductFormButtons &&
        newProductFormButtons
      ) {
        morph(
          currentProductFormButtons,
          newProductFormButtons
        );

        const newQuantitySelector =
          /** @type {any} */ (
            this.querySelector(
              'quantity-selector-component'
            )
          );

        const newQuantityInputElement =
          /** @type {HTMLInputElement | null} */ (
            event.detail.data.html.querySelector(
              'quantity-selector-component input[ref="quantityInput"]'
            )
          );

        if (
          newQuantitySelector
            ?.updateConstraints &&
          newQuantityInputElement &&
          currentQuantityValue
        ) {
          newQuantitySelector.setValue(
            currentQuantityValue
          );

          newQuantitySelector
            .updateConstraints(
              newQuantityInputElement.min,
              newQuantityInputElement.max ||
                null,
              newQuantityInputElement.step
            );
        }
      }
    } else {
      const quantityLabel =
        this.querySelector(
          '.quantity-label'
        );

      const newQuantityLabel =
        event.detail.data.html
          .querySelector(
            '.quantity-label'
          );

      this.#morphOrUpdateElement(
        quantityLabel,
        newQuantityLabel,
        quantitySelector
      );

      const addToCartButton =
        this.querySelector(
          '[ref="addToCartButtonContainer"]'
        );

      this.#morphOrUpdateElement(
        quantityRules,
        newQuantityRules,
        addToCartButton
      );
    }

    /**
     * Sync cart quantity for newly selected variant.
     */
    await this.#fetchAndUpdateCartQuantity();
  };

  /**
   * Disable native ATC while variant UI updates.
   */
  #onVariantSelected = () => {
    this.refs
      .addToCartButtonContainer
      ?.disable();
  };
}

if (
  !customElements.get(
    'product-form-component'
  )
) {
  customElements.define(
    'product-form-component',
    ProductFormComponent
  );
}